/**
 * A tabela das listas. As seis telas de lista repetem uma anatomia só — o que muda é a
 * identificação da linha e o bloco próprio de cada tela.
 *
 * O cabeçalho é o mesmo da tabela da Meta Geral: o rótulo ordena (maior → menor → ordem da API) e
 * o funil ao lado abre mínimo/máximo da coluna. Os filtros ativos viram chips acima da tabela.
 *
 * Três regras do contrato estão implementadas aqui, não nas telas:
 * - célula sem valor mostra "—" (quem formata já devolve isso), e **ausente ordena por último**,
 *   para os dois sentidos: "—" não é nem o maior nem o menor, é outra coisa;
 * - a ordem que a API mandou é a ordem certa por padrão — ordenar é recurso do usuário, não correção;
 * - duas taxas que só diferem no denominador ganham o denominador no cabeçalho quando aparecem juntas.
 */
import { Fragment, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { ChevronRight, Filter } from 'lucide-react';
import { FilterChips } from '@/components/v2/FilterChips';
import { seg, v } from '@/components/v2/theme';
import { BLOCK_HUE, BLOCK_LABEL, BLOCKS, M, headerLabel, readMetric, type Block } from '../lib/metrics';
import { c } from '@/components/v2/theme';
import type { Fmtr } from '../lib/format';
import type { SortPref } from '../lib/prefs';

export interface Col<R> {
    k: string;
    l: string;
    /** Entra no cabeçalho em letra menor: o denominador da taxa. */
    den?: string;
    align?: 'l' | 'r';
    width?: number;
    title?: string;
    cell: (row: R) => ReactNode;
    /** Valor de ordenação; `null` vai sempre para o fim. */
    value?: (row: R) => number | string | null;
    /** Coluna numérica: ganha o funil de mínimo/máximo. */
    num?: boolean;
    /** Unidade, para o cabeçalho do popover ("em R$", "em %", "quantidade"). */
    unit?: string;
    /** Bloco de origem (Receita · Envios · Taxas), para a faixa de "Separar por origem". */
    group?: Block;
}

export interface ColFilter { min: string; max: string }

/** "1234,5" → 1234.5. Vírgula ou ponto; vazio e lixo viram null (filtro inativo). */
const toNumber = (s: string): number | null => {
    const n = parseFloat(s.trim().replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

const isActive = (f?: ColFilter) => !!f && (toNumber(f.min) != null || toNumber(f.max) != null);

const UNIT: Record<string, string> = { brl: 'em R$', pct1: 'em %', int: 'quantidade' };

/** Colunas de métrica a partir das chaves escolhidas pelo usuário. */
export function metricCols<R>(
    keys: readonly string[],
    fmt: Fmtr,
    pick: (row: R) => Parameters<typeof readMetric>[0],
    opts: { derived?: (row: R) => boolean } = {},
): Col<R>[] {
    return keys.filter((k) => M[k]).map((k) => {
        const met = M[k];
        const { l, den } = headerLabel(k, keys);
        return {
            k,
            l,
            den,
            align: 'r' as const,
            num: true,
            group: met.block,
            unit: UNIT[met.fmt],
            title: met.d,
            value: (row: R) => readMetric(pick(row), k),
            cell: (row: R) => {
                const value = readMetric(pick(row), k);
                const text = fmt.metric(met, value);
                // taxa recalculada ganha a marca; sem valor, não há o que marcar
                const mark = met.block === 'rates' && value != null && opts.derived?.(row);
                return (
                    <span style={value == null ? { color: v('faint') } : undefined}>
                        {text}{mark ? <abbr title="Recalculada a partir dos totais somados dos projetos" style={{ textDecoration: 'none', color: v('faint') }}>*</abbr> : null}
                    </span>
                );
            },
        };
    });
}

/**
 * A ordenação da lista, de fora da tabela: os cards mostram as mesmas linhas, na mesma ordem.
 * Sem `sort`, a ordem da API é a ordem certa.
 */
export function sortRows<R>(rows: R[], cols: Col<R>[], sort: SortPref | null): R[] {
    if (!sort) return rows;
    const col = cols.find((x) => x.k === sort.key);
    if (!col?.value) return rows;
    return [...rows].sort((a, b) => cmp(col.value!(a), col.value!(b), sort.dir));
}

const cmp = (a: number | string | null, b: number | string | null, dir: 'asc' | 'desc') => {
    if (a == null && b == null) return 0;
    if (a == null) return 1; // ausente por último nos dois sentidos
    if (b == null) return -1;
    const d = typeof a === 'string' || typeof b === 'string'
        ? String(a).localeCompare(String(b), 'pt-BR')
        : a - b;
    return dir === 'asc' ? d : -d;
};

export interface MetricTableProps<R> {
    rows: R[];
    cols: Col<R>[];
    rowKey: (row: R, i: number) => string;
    /** Ordenação controlada (salva nas preferências). Sem `onSort`, a tabela guarda a ordem sozinha. */
    sort?: SortPref | null;
    onSort?: (sort: SortPref | null) => void;
    /** Devolve o conteúdo expandido da linha, ou null quando ela não abre. */
    children?: (row: R) => ReactNode;
    /** Rótulo do botão de expandir ("3 peças"). */
    expandLabel?: (row: R) => string | null;
    /** Linha de total, quando a tela tem uma. */
    footer?: ReactNode;
    /** Sem filtro de coluna (tabelas aninhadas, onde o funil seria ruído). */
    noFilters?: boolean;
    /** "Separar por origem": faixa colorida por bloco no cabeçalho e fundo na coluna. */
    grouped?: boolean;
    /** Clique na linha: abre o detalhe dela. Sem isso a linha não é clicável. */
    onPick?: (row: R) => void;
}

export function MetricTable<R>({ rows, cols: rawCols, rowKey, sort: sortProp, onSort, children, expandLabel, footer, noFilters, grouped, onPick }: MetricTableProps<R>) {
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [filters, setFilters] = useState<Record<string, ColFilter>>({});
    const [pop, setPop] = useState<{ k: string; x: number; y: number } | null>(null);
    // sem `onSort` a ordenação é da própria tabela: clicar no cabeçalho tem de fazer alguma coisa
    // em toda tabela, senão o rótulo clicável vira um botão morto
    const [ownSort, setOwnSort] = useState<SortPref | null>(sortProp ?? null);
    const sort = onSort ? sortProp ?? null : ownSort;
    const setSort = onSort ?? setOwnSort;
    const expandable = !!children && !!expandLabel;

    /**
     * Com "Separar por origem" ligado a tabela agrupa de verdade: as colunas de número trocam de
     * lugar entre si para cada bloco ficar inteiro, mantendo a ordem escolhida dentro do bloco. As
     * colunas que não são de número (identificação, situação) não saem do lugar.
     */
    const cols = useMemo(() => {
        if (!grouped) return rawCols;
        const slots = rawCols.map((col, i) => (col.group ? i : -1)).filter((i) => i >= 0);
        const metric = slots
            .map((i) => rawCols[i])
            .sort((a, b) => BLOCKS.indexOf(a.group!) - BLOCKS.indexOf(b.group!));
        const out = [...rawCols];
        slots.forEach((slot, j) => { out[slot] = metric[j]; });
        return out;
    }, [rawCols, grouped]);

    const active = useMemo(
        () => Object.entries(filters).filter(([, f]) => isActive(f)),
        [filters],
    );

    const visible = useMemo(() => {
        if (!active.length) return rows;
        return rows.filter((row) => active.every(([k, f]) => {
            const col = cols.find((c) => c.k === k);
            if (!col?.value) return true;
            const value = col.value(row);
            // linha sem valor não satisfaz um filtro numérico: "—" não é zero nem infinito
            if (typeof value !== 'number') return false;
            const min = toNumber(f.min);
            const max = toNumber(f.max);
            return (min == null || value >= min) && (max == null || value <= max);
        }));
    }, [rows, cols, active]);

    const sorted = useMemo(() => sortRows(visible, cols, sort), [visible, cols, sort]);

    const toggleSort = (k: string) => {
        if (sort?.key !== k) return setSort({ key: k, dir: 'desc' });
        if (sort.dir === 'desc') return setSort({ key: k, dir: 'asc' });
        return setSort(null); // terceiro clique devolve a ordem da API
    };

    const openFilter = (k: string) => (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        const r = e.currentTarget.getBoundingClientRect();
        setPop({
            k,
            x: Math.max(8, Math.min(r.right - 250, window.innerWidth - 262)),
            y: Math.max(8, Math.min(r.bottom + 8, window.innerHeight - 300)),
        });
    };

    const setFilter = (k: string, patch: Partial<ColFilter>) =>
        setFilters((f) => ({ ...f, [k]: { ...(f[k] ?? { min: '', max: '' }), ...patch } }));

    const clearFilter = (k: string) => setFilters((f) => { const next = { ...f }; delete next[k]; return next; });

    const chips = active.map(([k, f]) => {
        const col = cols.find((c) => c.k === k);
        const min = toNumber(f.min);
        const max = toNumber(f.max);
        return {
            id: k,
            text: `${col?.l ?? k}${min != null ? ` ≥ ${f.min}` : ''}${max != null ? ` ≤ ${f.max}` : ''}`,
        };
    });

    return (
        <>
            {chips.length > 0 && (
                <div style={{ padding: '10px 18px 0' }}>
                    <FilterChips chips={chips} onRemove={(c) => clearFilter(c.id)} />
                </div>
            )}
            {active.length > 0 && visible.length !== rows.length && (
                <div style={{ padding: '6px 18px 0', fontSize: 11.5, color: v('faint') }}>
                    {visible.length} de {rows.length} linhas passam no filtro · linhas sem valor na coluna filtrada ficam de fora
                </div>
            )}
            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            {expandable && <th style={{ ...th, width: 34 }} aria-label="Abrir" />}
                            {cols.map((col, ci) => {
                                const on = sort?.key === col.k;
                                const sortable = !!col.value;
                                const filtered = isActive(filters[col.k]);
                                const showFilter = !noFilters && col.num && sortable;
                                const band = grouped && col.group ? BLOCK_HUE[col.group] : null;
                                // o rótulo do grupo sai só na primeira coluna de uma sequência: repetido
                                // em todas, vira ruído em vez de faixa
                                const firstOfGroup = !!col.group && cols[ci - 1]?.group !== col.group;
                                // a célula inteira ordena: o alvo do clique é a área que o olho vê como
                                // cabeçalho, não só as letras. O funil para o clique antes dele.
                                return (
                                    <th key={col.k} onClick={sortable ? () => toggleSort(col.k) : undefined}
                                        style={{
                                            ...th, width: col.width, textAlign: col.align === 'r' ? 'right' : 'left',
                                            cursor: sortable ? 'pointer' : 'default',
                                            ...(band ? { borderTop: `2px solid ${c(band)}`, background: c(band, 0.07) } : {}),
                                        }}
                                        aria-sort={on ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                                        {grouped && (
                                            <div style={{ height: 14, fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: band ? c(band) : 'transparent', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                                {firstOfGroup && col.group ? BLOCK_LABEL[col.group] : ''}
                                            </div>
                                        )}
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%', minWidth: 0, flexDirection: col.align === 'r' ? 'row-reverse' : 'row' }}>
                                            <span className={sortable ? 'rv-h-text' : undefined} title={col.title}
                                                style={{ color: on || filtered ? v('text') : 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                                                {col.l}
                                                {col.den && <span style={{ color: v('faint'), fontWeight: 400 }}> {col.den}</span>}
                                                {on ? (sort!.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                                            </span>
                                            {showFilter && (
                                                <span onClick={openFilter(col.k)} title="Filtrar" role="button" tabIndex={0}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFilter(col.k)(e as unknown as MouseEvent<HTMLElement>); } }}
                                                    style={{ display: 'inline-flex', flexShrink: 0, cursor: 'pointer', color: filtered ? v('text') : v('line') }}>
                                                    <Filter size={11} />
                                                </span>
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row, i) => {
                            const key = rowKey(row, i);
                            const label = expandable ? expandLabel!(row) : null;
                            const isOpen = !!open[key];
                            return (
                                <Fragment key={key}>
                                    <tr {...(onPick ? { onClick: () => onPick(row), className: 'rv-h-row', style: { cursor: 'pointer' }, title: 'Abrir o detalhe desta linha' } : {})}>
                                        {expandable && (
                                            <td style={{ ...td, textAlign: 'left' }}>
                                                {label && (
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => ({ ...o, [key]: !o[key] })); }}
                                                        aria-expanded={isOpen} title={label}
                                                        style={{ border: 0, background: 'transparent', color: v('muted'), cursor: 'pointer', padding: 2, display: 'flex' }}>
                                                        <ChevronRight size={15} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                        {cols.map((col) => {
                                            const band = grouped && col.group ? BLOCK_HUE[col.group] : null;
                                            return (
                                                <td key={col.k} style={{ ...td, textAlign: col.align === 'r' ? 'right' : 'left', ...(band ? { background: c(band, 0.05) } : {}) }}>
                                                    {col.cell(row)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {isOpen && label && (
                                        <tr>
                                            <td colSpan={cols.length + 1} style={{ padding: 0, background: v('inset'), borderBottom: `1px solid ${v('border-soft')}` }}>
                                                {children!(row)}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                        {!sorted.length && (
                            <tr>
                                <td colSpan={cols.length + (expandable ? 1 : 0)} style={{ ...td, textAlign: 'center', color: v('faint'), padding: '28px 12px' }}>
                                    Nenhuma linha passa no filtro das colunas.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {footer && (
                        <tfoot>
                            <tr>
                                <td colSpan={cols.length + (expandable ? 1 : 0)} style={{ padding: 0 }}>{footer}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
            {pop && (
                <FilterPopover
                    col={cols.find((c) => c.k === pop.k)}
                    at={pop}
                    filter={filters[pop.k] || { min: '', max: '' }}
                    sort={sort}
                    onSort={setSort}
                    onChange={(patch) => setFilter(pop.k, patch)}
                    onClear={() => clearFilter(pop.k)}
                    onClose={() => setPop(null)}
                />
            )}
        </>
    );
}

/** Mesmo popover da tabela da Meta Geral: ordenar + mínimo/máximo + Limpar/Aplicar. */
function FilterPopover<R>({ col, at, filter, sort, onSort, onChange, onClear, onClose }: {
    col: Col<R> | undefined;
    at: { k: string; x: number; y: number };
    filter: ColFilter;
    sort: SortPref | null;
    onSort: (s: SortPref | null) => void;
    onChange: (patch: Partial<ColFilter>) => void;
    onClear: () => void;
    onClose: () => void;
}) {
    if (!col) return null;
    const dirs: ['desc' | 'asc', string][] = [['desc', 'Maior → menor'], ['asc', 'Menor → maior']];
    return (
        <>
            <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
            <div style={{
                position: 'fixed', left: at.x, top: at.y, zIndex: 81, width: 250, background: v('popover'),
                border: `1px solid ${v('border-3')}`, borderRadius: 12, padding: 14, display: 'flex',
                flexDirection: 'column', gap: 12, boxShadow: 'var(--rv-shadow)', fontSize: 13,
            }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontWeight: 600 }}>{col.l}</span>
                    <span style={{ fontSize: 12, color: v('faint') }}>{col.unit ?? 'quantidade'}</span>
                </div>
                <div style={{ display: 'flex', gap: 2, padding: 3, background: v('surface'), border: `1px solid ${v('border')}`, borderRadius: 9 }}>
                    {dirs.map(([dir, label]) => {
                        const on = sort?.key === at.k && sort.dir === dir;
                        return (
                            <button key={dir} type="button" onClick={() => onSort(on ? null : { key: at.k, dir })}
                                style={{ flex: 1, height: 26, border: 0, borderRadius: 6, ...seg(on), fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                                {label}
                            </button>
                        );
                    })}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: v('muted') }}>
                        Mínimo
                        <input value={filter.min} onChange={(e) => onChange({ min: e.target.value })} placeholder="—" inputMode="decimal" style={inputSt} />
                    </label>
                    <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: v('muted') }}>
                        Máximo
                        <input value={filter.max} onChange={(e) => onChange({ max: e.target.value })} placeholder="—" inputMode="decimal" style={inputSt} />
                    </label>
                </div>
                <div style={{ fontSize: 11.5, color: v('faint'), lineHeight: 1.45 }}>
                    Use vírgula ou ponto para decimais. Linhas com “—” nesta coluna ficam de fora.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => { onClear(); onClose(); }}
                        style={{ height: 30, padding: '0 12px', border: `1px solid ${v('border-3')}`, borderRadius: 8, background: 'transparent', color: v('text-2'), fontSize: 12.5, cursor: 'pointer' }}>
                        Limpar
                    </button>
                    <button type="button" onClick={onClose}
                        style={{ marginLeft: 'auto', height: 30, padding: '0 14px', border: 0, borderRadius: 8, background: v('text'), color: v('bg'), fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                        Aplicar
                    </button>
                </div>
            </div>
        </>
    );
}

const inputSt = {
    height: 32, padding: '0 10px', background: v('surface'), border: `1px solid ${v('border-2')}`,
    borderRadius: 8, color: v('text'), font: 'inherit', fontSize: 13, outline: 'none', width: '100%',
} as const;

const th = {
    fontSize: 11.5, fontWeight: 500, color: v('faint'), padding: '9px 12px',
    borderBottom: `1px solid ${v('border')}`, whiteSpace: 'nowrap', background: v('surface'),
    position: 'sticky' as const, top: 0, zIndex: 1,
};

const td = {
    fontSize: 13, padding: '11px 12px', borderBottom: `1px solid ${v('border-soft')}`,
    whiteSpace: 'nowrap' as const, fontVariantNumeric: 'tabular-nums' as const,
};
